import { TableV3RangeFilterOptions } from 'common/table/types/TableV3';

export enum CandidateFilterMatchMode {
  INCLUDE_ANY = 'INCLUDE_ANY',
  INCLUDE_ALL = 'INCLUDE_ALL',
  EXCLUDE = 'EXCLUDE'
}

export enum CandidateFilterTypeKey {
  TEXT = 'text',
  TEXT_ARRAY = 'textArray',
  NUMBER = 'number',
  DATE = 'date',
  BOOLEAN = 'boolean',
  GEO = 'geo'
}

export type CandidateTextFilter = {
  equal?: string;
  like?: string;
  likeAny?: string[];
  containsAny?: string[];
  containsNone?: string[];
};

export type CandidateTextArrayFilter = {
  containsAny?: string[];
  containsAll?: string[];
  containsNone?: string[];
};

export type CandidateNumberFilter = {
  equal?: number;
  greaterOrEqual?: number;
  lessOrEqual?: number;
};

export type CandidateDateFilter = {
  equal?: string;
  greaterOrEqual?: string;
  lessOrEqual?: string;
  greaterThan?: string;
  lessThan?: string;
};

export type CandidateBooleanFilter = {
  equal?: boolean;
};

export type CandidateGeoFilter = {
  latitude: number;
  longitude: number;
  radiusMiles: number;
  // The place the coordinates came from, when the service resolved one. The
  // only human-readable part of a geo condition.
  name?: string;
};

export type CandidateFilterCondition = {
  field: string;
  vectorDbKey?: string;
  [CandidateFilterTypeKey.TEXT]?: CandidateTextFilter;
  [CandidateFilterTypeKey.TEXT_ARRAY]?: CandidateTextArrayFilter;
  [CandidateFilterTypeKey.NUMBER]?: CandidateNumberFilter;
  [CandidateFilterTypeKey.DATE]?: CandidateDateFilter;
  [CandidateFilterTypeKey.BOOLEAN]?: CandidateBooleanFilter;
  [CandidateFilterTypeKey.GEO]?: CandidateGeoFilter;
};

export type CandidateFilterCombinator = {
  and?: CandidateFilterNode[];
  or?: CandidateFilterNode[];
  not?: CandidateFilterNode;
};

export type CandidateFilterNode =
  | CandidateFilterCondition
  | CandidateFilterCombinator;

export enum CandidateFilterValueType {
  TEXT = 'TEXT',
  TEXT_ARRAY = 'TEXT_ARRAY',
  NUMBER = 'NUMBER',
  DATE = 'DATE',
  BOOLEAN = 'BOOLEAN',
  GEO = 'GEO'
}

// A list, not a map keyed by column: one field can carry more than one
// condition at once (include-any plus exclude).
export type CandidateSelectionFilter = {
  id: string;
  field: string;
  vectorDbKey?: string;
  type: CandidateFilterValueType.TEXT | CandidateFilterValueType.TEXT_ARRAY;
  mode: CandidateFilterMatchMode;
  values: string[];
  valueLabels?: Record<string, string>;
};

export type CandidateRangeFilter = {
  id: string;
  field: string;
  vectorDbKey?: string;
  type: CandidateFilterValueType.NUMBER | CandidateFilterValueType.DATE;
  min?: number | string;
  max?: number | string;
};

export type CandidateBooleanSelectionFilter = {
  id: string;
  field: string;
  vectorDbKey?: string;
  type: CandidateFilterValueType.BOOLEAN;
  value: boolean;
};

export type CandidateGeoSelectionFilter = {
  id: string;
  field: string;
  type: CandidateFilterValueType.GEO;
  latitude: number;
  longitude: number;
  radiusMiles: number;
  /** Where the coordinates came from. A radius has nothing else to show. */
  name?: string;
};

export type RangeFilterSettings = TableV3RangeFilterOptions & {
  showsAge?: boolean;
};

// Universal fields carry no vectorDbKey; custom fields must.
export type SourcingFilterField = {
  field: string;
  type: CandidateFilterValueType;
  label?: string;
  vectorDbKey?: string;
  allowedValues?: string[];
  range?: RangeFilterSettings;
};

export type SourcingSearchRequest = {
  // Both are derived server-side from the caller's verified token; sending
  // them would not change the search and cannot widen its scope.
  organizationId?: string;
  userId?: string;
  availableFilters: SourcingFilterField[];
  id?: string;
  messageBody?: string;
  filter?: CandidateFilterNode;
  includeExplanations?: boolean;
  likeCandidateNames?: string[];
  skipReply?: boolean;
};

export type SourcingSearchMessage = {
  id: string;
  sender: 'USER' | 'AGENT';
  body: string;
  createdAt?: string | null;
};

export type SourcingSearchMode =
  | 'semantic'
  | 'boolean'
  | 'fetch'
  | 'similar'
  | 'questions'
  | 'none';

export type SourcingSearchResponse = {
  id: string | null;
  candidates: Record<string, unknown>[];
  totalCandidatesCount: number;
  newUserMessage: SourcingSearchMessage | null;
  newAgentMessage: SourcingSearchMessage | null;
  appliedFilter: CandidateFilterNode | null;
  // What the model derived from the message, separate from appliedFilter so
  // the caller can tell its own filters from the model's.
  generatedFilter?: CandidateFilterNode | null;
  generatedSemanticQuery?: string | null;
  likeCandidateNames?: string[];
  /** Fields the recruiter asked, in words, to drop; not applied to this search. */
  removedFilterFields?: string[];
  searchMode: SourcingSearchMode;
  bias: {
    query_is_biased: boolean;
    message?: string | null;
  } | null;
  filters: Record<string, unknown>;
};

export type MatchExplanationsRequest = {
  id: string;
  /** In display order; the order is the ranking. */
  candidateProfileIds: string[];
  semanticQuery: string;
  recruiterMessage?: string | null;
  /** Rank of the first id within the whole result set. */
  rankOffset?: number;
};

export type SourcingReplyRequest = {
  id: string;
  userMessageId?: string | null;
  candidateProfileIds: string[];
  totalCandidatesCount?: number;
  returnedCandidatesCount?: number;
  semanticQuery?: string | null;
  booleanQuery?: string | null;
  generatedFilter?: CandidateFilterNode | null;
  appliedFilter?: CandidateFilterNode | null;
  likeCandidateNames?: string[];
};

export type SourcingReplyResponse = {
  newAgentMessage: SourcingSearchMessage | null;
};

export type ExplanationContext = {
  searchId: string;
  semanticQuery: string;
  recruiterMessage: string | null;
};

export type MatchExplanationsResponse = {
  /** Null means none is coming. */
  explanations: Record<string, string | null>;
};

export type CandidateFilterState =
  | CandidateSelectionFilter
  | CandidateRangeFilter
  | CandidateBooleanSelectionFilter
  | CandidateGeoSelectionFilter;

// The Or groups, as condition ids, in rail order.
export type CandidateFilterGroups = string[][];
